import {
  AdminNotificationsPage,
  AdminNotificationsQuery,
  AdminNotificationsResponse,
  CreateNotificationPayload,
  CreateNotificationResponse,
  NotificationApiRecord,
  NotificationDraft,
  NotificationItem,
  NotificationPriority,
  NotificationSeverity,
  NotificationType,
  UpdateNotificationPayload,
  UserNotificationsQuery,
} from "./notifications.types";

const SUPPORTED_CLOUD_PROVIDERS: ReadonlySet<string> = new Set(["aws", "gcp", "azure", "oci", "drcc"]);

export function normalizeAudienceValue(value: string): string {
  return value.trim().toLowerCase();
}

export function getAudienceLocalMarkets(currentTenant: string, mappings: Map<string, string>): Set<string> {
  const localMarkets: Set<string> = new Set<string>();
  const mappedLocalMarket: string | undefined = mappings.get(currentTenant);

  if (mappedLocalMarket) {
    localMarkets.add(normalizeAudienceValue(mappedLocalMarket));
  }

  return localMarkets;
}

export function getNotificationsForAudience(
  notifications: NotificationItem[],
  currentTenant: string | null | undefined,
  mappings: Map<string, string>,
  currentPagePath?: string | null,
  currentUserEmail?: string | null,
): NotificationItem[] {
  const normalizedCurrentTenant: string = normalizeAudienceValue(currentTenant ?? "");
  const audienceLocalMarkets: Set<string> = normalizedCurrentTenant
    ? getAudienceLocalMarkets(currentTenant, mappings)
    : new Set<string>();
  const normalizedCurrentUserEmail: string = normalizeAudienceValue(currentUserEmail ?? "");
  const normalizedCurrentPath: string = normalizePath(currentPagePath ?? "");
  const currentCloudProvider: string | null = extractCloudProviderFromPath(normalizedCurrentPath);

  return notifications.filter((notification) => {
    if (notification.tenants.length > 0 && !normalizedCurrentTenant) {
      return false;
    }

    if (notification.tenants.length > 0) {
      const tenantMatchesCurrentTenant: boolean = notification.tenants.some(
        (tenant) => normalizeAudienceValue(tenant) === normalizedCurrentTenant,
      );

      if (!tenantMatchesCurrentTenant) {
        return false;
      }
    }

    if (notification.local_markets.length > 0 && audienceLocalMarkets.size > 0) {
      const localMarketMatchesAudience: boolean = notification.local_markets.some((localMarket) =>
        audienceLocalMarkets.has(normalizeAudienceValue(localMarket)),
      );

      if (!localMarketMatchesAudience) {
        return false;
      }
    }

    if (notification.target_users.length > 0) {
      if (!normalizedCurrentUserEmail) {
        return false;
      }

      const targetUserMatchesCurrentUser: boolean = notification.target_users.some(
        (targetUser) => normalizeAudienceValue(targetUser) === normalizedCurrentUserEmail,
      );

      if (!targetUserMatchesCurrentUser) {
        return false;
      }
    }

    if (notification.cloud_provider.length > 0) {
      if (!isNotificationVisibleForCloudProvider(notification.cloud_provider, currentCloudProvider)) {
        return false;
      }
    }

    return doesNotificationMatchCurrentPage(notification.page, normalizedCurrentPath);
  });
}

function extractCloudProviderFromPath(path: string): string | null {
  const normalizedPath: string = normalizePath(path);
  if (!normalizedPath) {
    return null;
  }

  const pathSegments: string[] = normalizedPath.split("/").filter(Boolean);

  if (pathSegments[0] === "vcloudsmart") {
    const vcloudsmartProviderSegment: string | undefined = [pathSegments[1], pathSegments[2]].find(
      (segment): segment is string => !!segment && SUPPORTED_CLOUD_PROVIDERS.has(segment),
    );

    if (vcloudsmartProviderSegment) {
      return vcloudsmartProviderSegment;
    }
  }

  if (pathSegments[0] === "scheduler" && pathSegments[1] && SUPPORTED_CLOUD_PROVIDERS.has(pathSegments[1])) {
    return pathSegments[1];
  }

  return null;
}

function isNotificationVisibleForCloudProvider(cloudProviders: string[], currentCloudProvider: string | null): boolean {
  const normalizedProviders: string[] = cloudProviders
    .map((provider) => normalizeAudienceValue(provider))
    .filter((provider) => provider.length > 0);

  if (normalizedProviders.length === 0) {
    return true;
  }

  return !!currentCloudProvider && normalizedProviders.includes(currentCloudProvider);
}

function doesNotificationMatchCurrentPage(notificationPages: string[], normalizedPath: string): boolean {
  const normalizedPageValues: string[] = notificationPages
    .map((page) => normalizeAudienceValue(page))
    .filter((page) => page.length > 0);

  if (normalizedPageValues.length === 0) {
    return true;
  }

  if (!normalizedPath) {
    return true;
  }

  const normalizedPathWithTrailingSlash: string = `${normalizedPath}/`;
  const normalizedPathSlug: string = toSlug(normalizedPath);

  return normalizedPageValues.some((normalizedPageValue) => {
    if (normalizedPageValue === "*" || normalizedPageValue === "all" || normalizedPageValue === "any") {
      return true;
    }

    if (isPathOrUrlValue(normalizedPageValue)) {
      const normalizedPagePath: string = normalizePath(normalizedPageValue);
      if (!normalizedPagePath) {
        return false;
      }

      return (
        normalizedPath === normalizedPagePath || normalizedPathWithTrailingSlash.startsWith(`${normalizedPagePath}/`)
      );
    }

    const normalizedPageSlug: string = toSlug(normalizedPageValue);
    return !!normalizedPageSlug && normalizedPathSlug.includes(normalizedPageSlug);
  });
}

function isPathOrUrlValue(value: string): boolean {
  return value.startsWith("/") || /^https?:\/\//i.test(value) || value.includes("/");
}

function normalizePath(value: string): string {
  const trimmed: string = value.trim();
  if (!trimmed) {
    return "";
  }

  let pathname: string = trimmed;

  if (/^https?:\/\//i.test(pathname)) {
    try {
      pathname = new URL(pathname).pathname || "";
    } catch {
      return "";
    }
  }

  pathname = pathname.split("#")[0].split("?")[0];

  try {
    pathname = decodeURIComponent(pathname);
  } catch {
    return "";
  }

  if (!pathname.startsWith("/")) {
    return "";
  }

  pathname = pathname
    .replace(/\/+$/g, "")
    .replace(/\/{2,}/g, "/")
    .toLowerCase();

  return pathname === "/" ? "/" : pathname;
}

function toSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function getExpirationStatus(notification: NotificationItem): string {
  if (!notification.expires_at) {
    return "";
  }

  const expirationTime: number = new Date(notification.expires_at).getTime();

  if (Number.isNaN(expirationTime)) {
    return "";
  }

  const timeDifference: number = expirationTime - Date.now();

  if (timeDifference <= 0) {
    return "Expired";
  }

  const hoursRemaining: number = Math.max(1, Math.floor(timeDifference / (1000 * 60 * 60)));

  if (hoursRemaining >= 24) {
    const daysRemaining: number = Math.max(1, Math.floor(hoursRemaining / 24));
    return `Expires in ${daysRemaining}d`;
  }

  return `Expires in ${hoursRemaining}h`;
}

export function getActiveNotifications(notifications: NotificationItem[]): NotificationItem[] {
  return notifications
    .filter((notification) => !isExpired(notification))
    .sort((left, right) => right.timestamp.localeCompare(left.timestamp));
}

export function normalizeNotification(
  notification: Partial<NotificationItem> | undefined,
  draft: NotificationDraft,
): NotificationItem {
  return {
    id: notification?.id ?? `notification-${Date.now()}`,
    timestamp: notification?.timestamp ?? new Date().toISOString(),
    headline: notification?.headline ?? draft.headline,
    type: notification?.type ?? draft.type,
    severity: notification?.severity ?? draft.severity,
    priority: notification?.priority ?? draft.priority,
    unread: notification?.unread ?? draft.unread,
    description: notification?.description ?? draft.description,
    page: notification?.page ?? draft.page,
    type_of_data: notification?.type_of_data ?? draft.type_of_data,
    cloud_provider: notification?.cloud_provider ?? draft.cloud_provider,
    expired: notification?.expired ?? draft.expired,
    tenants: notification?.tenants ?? draft.tenants,
    local_markets: notification?.local_markets ?? draft.local_markets,
    action_params: notification?.action_params ?? draft.action_params,
    target_groups: notification?.target_groups ?? draft.target_groups,
    target_users: notification?.target_users ?? draft.target_users,
    expires_at: notification?.expires_at ?? draft.expires_at,
  };
}

export function mapDraftToPayload(
  draft: NotificationDraft & { notificationId?: string },
): CreateNotificationPayload & { notificationId?: string } {
  return {
    headline: draft.headline,
    type: draft.type,
    severity: draft.severity,
    priority: draft.priority,
    description: draft.description,
    page: draft.page,
    type_of_data: draft.type_of_data,
    cloud_provider: draft.cloud_provider,
    tenants: draft.tenants,
    local_markets: draft.local_markets,
    action_params: draft.action_params,
    target_groups: draft.target_groups,
    target_users: draft.target_users,
    expires_at: draft.expires_at,
    ...(draft.notificationId ? { notificationId: draft.notificationId } : {}),
  };
}

export function mapDraftToUpdatePayload(draft: NotificationDraft): UpdateNotificationPayload {
  return {
    type: draft.type,
    severity: draft.severity,
    description: draft.description,
    page: draft.page,
    type_of_data: draft.type_of_data,
    action_params: draft.action_params,
    expired: draft.expired,
    expires_at: draft.expires_at,
  };
}

export function extractNotificationList(
  response: NotificationApiRecord[] | { notifications?: NotificationApiRecord[] } | null | undefined,
): NotificationItem[] {
  if (Array.isArray(response)) {
    return response.map((notification) => normalizeApiNotification(notification));
  }

  if (Array.isArray(response?.notifications)) {
    return response.notifications.map((notification) => normalizeApiNotification(notification));
  }

  return [];
}

export function extractSingleNotification(response: unknown): NotificationItem | undefined {
  if (!isRecord(response)) {
    return undefined;
  }

  if (hasStringId(response)) {
    return response;
  }

  if (hasNotificationWithId(response)) {
    return response.notification;
  }

  return undefined;
}

export function extractCreatedNotificationIds(response: unknown): string[] {
  if (!isRecord(response) || !hasNotificationIds(response)) {
    return [];
  }

  return response.notification_ids.filter((item: unknown): item is string => typeof item === "string");
}

export function createAdminPage(
  notifications: NotificationItem[],
  response: AdminNotificationsResponse | NotificationApiRecord[] | { total?: number },
  requestedLimit: number,
  requestedOffset: number,
): AdminNotificationsPage {
  const responseMeta: Partial<AdminNotificationsResponse> | null = Array.isArray(response) ? null : response;
  const total: number = typeof responseMeta?.total === "number" ? responseMeta.total : notifications.length;
  const safeLimit: number = Math.max(1, requestedLimit);
  const pages: number =
    typeof responseMeta?.pages === "number" ? responseMeta.pages : Math.max(1, Math.ceil(total / safeLimit));
  const page: number =
    typeof responseMeta?.page === "number" ? responseMeta.page : Math.floor(requestedOffset / safeLimit) + 1;
  const more: boolean =
    typeof responseMeta?.more === "boolean" ? responseMeta.more : requestedOffset + notifications.length < total;
  return {
    notifications,
    total,
    page,
    pages,
    more,
    limit: requestedLimit,
    offset: requestedOffset,
  };
}

export function buildAdminNotificationsEndpoint(query: AdminNotificationsQuery, baseEndpoint: string): string {
  const params: URLSearchParams = new URLSearchParams();

  if (query.limit !== undefined) {
    params.set("limit", String(query.limit));
  }

  if (query.offset !== undefined) {
    params.set("offset", String(query.offset));
  }

  if (query.severity) {
    params.set("severity", query.severity);
  }

  if (query.type) {
    params.set("type", query.type);
  }

  if (query.priority) {
    params.set("priority", query.priority);
  }

  if (query.cloudProvider) {
    params.set("cloud_provider", query.cloudProvider);
  }

  if (query.expired !== undefined) {
    params.set("expired", String(query.expired));
  }

  if (query.tenant) {
    params.set("tenant", query.tenant);
  }

  const queryString: string = params.toString();
  return queryString.length > 0 ? `${baseEndpoint}?${queryString}` : baseEndpoint;
}

export function buildUserNotificationsEndpoint(query: UserNotificationsQuery, baseEndpoint: string): string {
  const params: URLSearchParams = new URLSearchParams();

  if (query.limit !== undefined) {
    params.set("limit", String(query.limit));
  }

  if (query.daysBack !== undefined) {
    params.set("days_back", String(query.daysBack));
  }

  if (query.includeRead !== undefined) {
    params.set("include_read", String(query.includeRead));
  }

  if (query.severity) {
    params.set("severity", query.severity);
  }

  if (query.type) {
    params.set("type", query.type);
  }

  if (query.priority) {
    params.set("priority", query.priority);
  }

  if (query.cloudProvider) {
    params.set("cloud_provider", query.cloudProvider);
  }

  const queryString: string = params.toString();
  return queryString.length > 0 ? `${baseEndpoint}?${queryString}` : baseEndpoint;
}

function isExpired(notification: NotificationItem): boolean {
  if (!notification.expires_at) {
    return false;
  }

  const expiresAt: Date = new Date(notification.expires_at);
  if (Number.isNaN(expiresAt.getTime())) {
    return false;
  }

  return expiresAt.getTime() <= Date.now();
}

function normalizeApiNotification(notification: NotificationApiRecord): NotificationItem {
  const normalizedType: NotificationType = normalizeType(notification.type);
  const normalizedDescription: string = asString(notification.description) || "Notification";
  const normalizedHeadline: string =
    asString(notification.headline) || asHeadline(normalizedDescription, normalizedType);
  const normalizedSeverity: NotificationSeverity = normalizeSeverity(notification.severity);
  const normalizedPriority: NotificationPriority = normalizePriority(notification.priority);

  return {
    id: asString(notification.id) || `notification-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    headline: normalizedHeadline,
    type: normalizedType,
    severity: normalizedSeverity,
    priority: normalizedPriority,
    unread: typeof notification.unread === "boolean" ? notification.unread : false,
    description: normalizedDescription,
    page: asStringArray(notification.page),
    timestamp: asString(notification.timestamp) || new Date().toISOString(),
    type_of_data: asStringArray(notification.type_of_data),
    cloud_provider: asStringArray(notification.cloud_provider),
    expired: typeof notification.expired === "boolean" ? notification.expired : false,
    tenants: asStringArray(notification.tenants),
    local_markets: asStringArray(notification.local_markets),
    action_params: {
      url: asString(notification.action_params?.url),
      label: asString(notification.action_params?.label) || "View Details",
    },
    target_groups: asStringArray(notification.target_groups),
    target_users: asStringArray(notification.target_users),
    expires_at: asString(notification.expires_at),
  };
}

function normalizeType(type: unknown): NotificationType {
  const value: string = asString(type).toLowerCase();
  if (!value) {
    return "system_announcement";
  }

  return value.replace(/\s+/g, "_");
}

function normalizeSeverity(severity: unknown): NotificationSeverity {
  const value: string = asString(severity).toLowerCase();
  if (value === "error" || value === "warning" || value === "info" || value === "success") {
    return value;
  }

  return "info";
}

function normalizePriority(priority: unknown): NotificationPriority {
  const value: string = asString(priority).toLowerCase();
  if (value === "urgent" || value === "high") {
    return "high";
  }

  if (value === "normal" || value === "medium") {
    return "medium";
  }

  if (value === "low") {
    return "low";
  }

  return "medium";
}

function asString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.reduce<string[]>((result, item) => {
    const normalizedItem: string = asString(item);
    if (normalizedItem) {
      result.push(normalizedItem);
    }
    return result;
  }, []);
}

function asHeadline(description: string, type: string): string {
  if (description.length > 0) {
    return description.length > 90 ? `${description.slice(0, 87)}...` : description;
  }

  return type.split("_").join(" ") || "Notification";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function hasStringId(value: Record<string, unknown>): value is NotificationItem & Record<string, unknown> {
  return typeof value.id === "string";
}

function hasNotificationWithId(value: Record<string, unknown>): value is { notification: NotificationItem } {
  return isRecord(value.notification) && typeof value.notification.id === "string";
}

function hasNotificationIds(
  value: Record<string, unknown>,
): value is (CreateNotificationResponse & { notification_ids: unknown[] }) & Record<string, unknown> {
  return Array.isArray(value.notification_ids);
}
